import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Alert, Box, LinearProgress, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { MILL_2 } from "@/setup/mill-config/mill-config";
import { TestShackReportFilters } from "../../shared/components/report/TestShackReportFilters";
import {
	type ImageKind,
	TestShackReportImageDialog,
} from "./components/TestShackReportImageDialog";
import { TestShackReportSectionRevList } from "./components/TestShackReportSectionRevList";
import { useTestShackReportData } from "../../hooks/useTestShackReportData";
import type {
	TestShackReportFilterOptions,
	TestShackReportInspection,
} from "../../models/TestShackReport";
import { groupTestShackReportBySectionRev } from "./utils/groupBySectionRev";

const EMPTY_FILTER_OPTIONS: TestShackReportFilterOptions = {
	sections: [],
	events: [],
	heats: [],
};

export const TestShackReportPage = () => {
	const [imageTarget, setImageTarget] = useState<{
		inspection: TestShackReportInspection;
		kind: ImageKind;
	} | null>(null);
	const {
		error,
		clearError,
		handleError,
		filters,
		handleFilterChange,
		filterOptionsQuery,
		reportQuery,
		inspections,
		isLoading,
		isQueryError,
	} = useTestShackReportData(MILL_2.id);
	const sectionRevs = groupTestShackReportBySectionRev(inspections);

	return (
		<>
			<HelmetDexteel title="Test Shack Report" />
			<Box sx={{ p: 2 }}>
				<Stack spacing={1.5}>
					<Typography variant="h5" fontWeight={800}>
						Test Shack Report
					</Typography>

					<TestShackReportFilters
						assetId={MILL_2.id}
						filters={filters}
						options={filterOptionsQuery.data ?? EMPTY_FILTER_OPTIONS}
						isLoadingOptions={filterOptionsQuery.isLoading}
						onChange={handleFilterChange}
					/>

					{isLoading && (
						<LinearProgress aria-label="Loading Test Shack report" />
					)}
					{isQueryError && (
						<Alert severity="error" role="alert">
							The Test Shack report could not be loaded. Review the error and
							retry the selected range.
						</Alert>
					)}
					{!isLoading &&
						!isQueryError &&
						reportQuery.data &&
						inspections.length === 0 && (
							<Alert severity="info" role="status">
								No inspections match the selected report filters.
							</Alert>
						)}

					<TestShackReportSectionRevList
						sectionRevs={sectionRevs}
						inspections={inspections}
						isLoading={isLoading}
						problemsOnly={filters.problemsOnly}
						showMinTargetMax={filters.showMinTargetMax}
						onProblemsOnlyChange={(problemsOnly) =>
							handleFilterChange({ problemsOnly })
						}
						onShowMinTargetMaxChange={(showMinTargetMax) =>
							handleFilterChange({ showMinTargetMax })
						}
						onViewImages={(inspection, kind) =>
							setImageTarget({ inspection, kind })
						}
						onError={handleError}
					/>
				</Stack>
			</Box>
			{imageTarget && (
				<TestShackReportImageDialog
					key={`${imageTarget.inspection.ProfileInspectionId}:${imageTarget.kind}`}
					inspection={imageTarget.inspection}
					initialKind={imageTarget.kind}
					onClose={() => setImageTarget(null)}
				/>
			)}
			<ErrorModal error={error} onHide={clearError} />
		</>
	);
};
